import { useState } from 'react'
import { useController } from 'react-hook-form'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useGetLiveListConfigs } from '@/services/hooks/relay/dynamic'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { ProjectGridField } from '@/types/fields'
import {
  FilterOp,
  StoredFilter,
  StoredFilterClause,
} from '@/types/live-list-filter'

type LiveListFilterConfigSettingProps = {
  field: ProjectGridField
  orgCredentialTypeId: string | null
}

export default function LiveListFilterConfigSetting({
  field,
  orgCredentialTypeId,
}: LiveListFilterConfigSettingProps) {
  const { field: controllerField } = useController({
    name: 'live_list_filter_config',
  })

  const { liveListConfigs, isPending } = useGetLiveListConfigs({
    orgCredentialTypeId: orgCredentialTypeId || '',
  })

  const matchingConfig = liveListConfigs?.live_list_configs.find(
    (cfg) =>
      !!field.info_type_id && cfg.info_types.includes(field.info_type_id),
  )

  const filterFields = matchingConfig?.filter_config.fields ?? []

  const currentConfig: StoredFilter | null = controllerField.value
  const clauses = currentConfig?.clauses ?? []

  const [newClauseField, setNewClauseField] = useState('')
  const [newClauseOp, setNewClauseOp] = useState<FilterOp | ''>('')
  const [newClauseValue, setNewClauseValue] = useState('')

  const selectedFilterField = filterFields.find(
    (f) => f.name === newClauseField,
  )

  function buildConfig(newClauses: StoredFilterClause[]): StoredFilter | null {
    if (!matchingConfig) return null
    return {
      resource: matchingConfig.filter_config.resource,
      schema_version: 1,
      resource_version: matchingConfig.filter_config.version,
      clauses: newClauses,
    }
  }

  function handleAddClause() {
    if (!newClauseField || !newClauseOp || !newClauseValue || !matchingConfig)
      return

    const newClause: StoredFilterClause = {
      field: newClauseField,
      op: newClauseOp,
      value: newClauseValue,
    }

    controllerField.onChange(buildConfig([...clauses, newClause]))
    setNewClauseField('')
    setNewClauseOp('')
    setNewClauseValue('')
  }

  function handleRemoveClause(index: number) {
    const newClauses = clauses.filter((_, i) => i !== index)
    controllerField.onChange(
      newClauses.length === 0 ? null : buildConfig(newClauses),
    )
  }

  const borderStyle = `dashed 1px ${SUPER_USER_ONLY_COLOR}`

  if (!field.info_type_id) {
    return (
      <Box sx={{ p: 2, border: borderStyle, borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Live List Filter Config
        </Typography>
        <Typography variant="caption" color="textSecondary">
          No info type set on this field — filter config is unavailable.
        </Typography>
      </Box>
    )
  }

  if (isPending) {
    return (
      <Box sx={{ p: 2, border: borderStyle, borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Live List Filter Config
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Loading live list configurations…
        </Typography>
      </Box>
    )
  }

  if (!matchingConfig) {
    return (
      <Box sx={{ p: 2, border: borderStyle, borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Live List Filter Config
        </Typography>
        <Typography variant="caption" color="textSecondary">
          No live list configuration registered for info type:{' '}
          <code>{field.info_type_id}</code>
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={2} sx={{ p: 2, border: borderStyle, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Typography variant="body2" color="textSecondary">
            Live List Filter Config
          </Typography>
          <Typography variant="caption" color="textSecondary">
            resource: <code>{matchingConfig.filter_config.resource}</code>
            {' · '}version: {matchingConfig.filter_config.version}
          </Typography>
        </Stack>
        {currentConfig && (
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={() => controllerField.onChange(null)}
          >
            Clear Filter
          </Button>
        )}
      </Stack>

      {clauses.length > 0 ? (
        <Stack spacing={0.5}>
          {clauses.map((clause, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', flexGrow: 1 }}
              >
                {clause.field} <strong>{clause.op}</strong> &quot;{clause.value}
                &quot;
              </Typography>
              <IconButton size="small" onClick={() => handleRemoveClause(i)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="textSecondary">
          No filter clauses — all records will be shown.
        </Typography>
      )}

      {/* Add clause row */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 1 }}
      >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Field</InputLabel>
          <Select
            value={newClauseField}
            label="Field"
            onChange={(e) => {
              setNewClauseField(e.target.value)
              setNewClauseOp('')
            }}
          >
            {filterFields.map((f) => (
              <MenuItem key={f.name} value={f.name}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 110 }}
          disabled={!newClauseField}
        >
          <InputLabel>Op</InputLabel>
          <Select
            value={newClauseOp}
            label="Op"
            onChange={(e) => setNewClauseOp(e.target.value as FilterOp)}
          >
            {(selectedFilterField?.ops ?? []).map((op) => (
              <MenuItem key={op} value={op}>
                {op}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Value"
          variant="outlined"
          value={newClauseValue}
          onChange={(e) => setNewClauseValue(e.target.value)}
          disabled={!newClauseField || !newClauseOp}
          sx={{ minWidth: 140 }}
        />

        <Button
          size="small"
          variant="outlined"
          onClick={handleAddClause}
          disabled={!newClauseField || !newClauseOp || !newClauseValue}
        >
          Add Clause
        </Button>
      </Stack>
    </Stack>
  )
}
